import type { WorkoutSessionDetails } from '../dto/WorkoutSessionDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { IdGenerator } from '../ports/IdGenerator';
import type { LocalDateProvider } from '../ports/LocalDateProvider';
import type { WorkoutPersistenceTransactions } from '../ports/WorkoutPersistenceTransactions';
import type { WorkoutRepository } from '../ports/WorkoutRepository';
import { buildSessionForMode } from '../../domain/workout/ExerciseCatalog';
import { WorkoutSessionStatus } from '../../domain/workout/enums';
import type { ExerciseLog } from '../../domain/workout/ExerciseLog';
import type { WorkoutSession } from '../../domain/workout/WorkoutSession';
import { toExerciseLogData, toWorkoutSessionData } from '../../domain/workout/WorkoutContent';
import { ExerciseStatus } from '../../domain/workout/enums';
import type { DayMode } from '../../domain/routine/enums';
import type { ExerciseLogId, ProfileId, WorkoutSessionId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

export class GetOrCreateTodayWorkout {
  public constructor(
    private readonly workouts: WorkoutRepository,
    private readonly transactions: WorkoutPersistenceTransactions,
    private readonly clock: Clock,
    private readonly dates: LocalDateProvider,
    private readonly ids: IdGenerator,
  ) {}

  public async execute(
    profileId: ProfileId,
    dayMode: DayMode,
  ): Promise<Result<WorkoutSessionDetails, RoutineError>> {
    try {
      const logicalDate = this.dates.today();
      const existing = await this.workouts.findByLogicalDate(profileId, logicalDate);
      if (existing) {
        const exercises = await this.transactions.listExercises(existing.id);
        return success({ session: existing, exercises });
      }

      const timestamp = this.clock.now().toISOString();
      const session: WorkoutSession = {
        id: this.ids.generate<WorkoutSessionId>(),
        profileId,
        logicalDate,
        status: WorkoutSessionStatus.pending,
        data: toWorkoutSessionData({ dayMode }),
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      const catalogExercises = buildSessionForMode(dayMode);
      const exercises = catalogExercises.map<ExerciseLog>((catalogExercise, index) => ({
        id: this.ids.generate<ExerciseLogId>(),
        profileId,
        workoutSessionId: session.id,
        order: index,
        data: toExerciseLogData({
          catalogId: catalogExercise.id,
          name: catalogExercise.name,
          equipment: catalogExercise.equipment,
          unit: catalogExercise.unit,
          targetValue: catalogExercise.targetValue,
          instructions: catalogExercise.instructions,
          supportsWeight: catalogExercise.supportsWeight,
          status: ExerciseStatus.pending,
          ...(catalogExercise.replacementId
            ? { replacementCatalogId: catalogExercise.replacementId }
            : {}),
        }),
        createdAt: timestamp,
        updatedAt: timestamp,
      }));

      const created = await this.transactions.createSessionWithExercisesIfAbsent(
        session,
        exercises,
      );
      if (!created) {
        const concurrent = await this.workouts.findByLogicalDate(profileId, logicalDate);
        if (!concurrent) {
          return failure(
            new RoutineError('workout_creation_failed', 'La séance de sport n’a pas pu être créée.'),
          );
        }
        const concurrentExercises = await this.transactions.listExercises(concurrent.id);
        return success({ session: concurrent, exercises: concurrentExercises });
      }
      return success({ session, exercises });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
